/**
 * File Tree View — Issue #557
 * Directory-based tree view of the import graph files.
 */

import { useState, useMemo } from 'react';
import type { ImportGraphData, ImportGraphNode } from './importGraphFlowConverter';
import { ROLE_COLORS } from './importGraphFlowConverter';

interface FileTreeViewProps {
  data: ImportGraphData;
  selectedFileId?: string;
  onSelectFile?: (fileId: string) => void;
  onViewInExplorer?: (fileId: string) => void;
  onViewSubgraph?: (fileId: string) => void;
  // Lifted filter state from page (issue #566)
  searchQuery?: string;
}

interface TreeNode {
  name: string;
  path: string;
  children: Map<string, TreeNode>;
  files: ImportGraphNode[];
}

function buildTree(nodes: ImportGraphNode[]): TreeNode {
  const root: TreeNode = { name: '', path: '', children: new Map(), files: [] };

  for (const node of nodes) {
    const parts = node.filePath.split('/').filter(Boolean);
    let current = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!current.children.has(part)) {
        const path = parts.slice(0, i + 1).join('/');
        current.children.set(part, { name: part, path, children: new Map(), files: [] });
      }
      current = current.children.get(part)!;
    }
    current.files.push(node);
  }

  return root;
}

function TreeNodeRow({
  node,
  depth,
  selectedFileId,
  onSelectFile,
  onViewInExplorer,
  onViewSubgraph,
}: {
  node: TreeNode;
  depth: number;
  selectedFileId?: string;
  onSelectFile?: (id: string) => void;
  onViewInExplorer?: (id: string) => void;
  onViewSubgraph?: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children.size > 0 || node.files.length > 0;
  const totalFiles = countFiles(node);

  return (
    <div>
      {/* Directory row */}
      {node.name && (
        <button
          onClick={() => setExpanded(v => !v)}
          className="w-full flex items-center gap-1.5 px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-800 text-left"
          style={{ paddingLeft: `${8 + depth * 16}px` }}
        >
          <span className="text-gray-400 text-xs">{expanded ? '▼' : '▶'}</span>
          <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
          <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">{node.name}/</span>
          <span className="ml-auto text-xs text-gray-400">{totalFiles}</span>
        </button>
      )}

      {/* Children */}
      {(!node.name || expanded) && hasChildren && (
        <div>
          {Array.from(node.children.values())
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(child => (
              <TreeNodeRow
                key={child.path}
                node={child}
                depth={depth + 1}
                selectedFileId={selectedFileId}
                onSelectFile={onSelectFile}
                onViewInExplorer={onViewInExplorer}
                onViewSubgraph={onViewSubgraph}
              />
            ))}
          {node.files
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(file => (
              <div
                key={file.id}
                className={`flex items-center gap-1.5 px-2 py-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 group ${
                  selectedFileId === file.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
                style={{ paddingLeft: `${8 + (depth + 1) * 16}px` }}
                onClick={() => onSelectFile?.(file.id)}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ROLE_COLORS[file.role] }} />
                <span className={`text-sm truncate ${selectedFileId === file.id ? 'text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                  {file.name}
                </span>
                {file.hasCircularDep && <span className="text-red-400 text-xs flex-shrink-0">⚠</span>}
                {file.isBarrel && <span className="text-cyan-400 text-xs flex-shrink-0">B</span>}
                <span className="ml-auto text-xs text-gray-400 flex-shrink-0">
                  {file.importedBy.length}↑ {file.imports.length}↓
                </span>
                {onViewInExplorer && (
                  <button
                    onClick={e => { e.stopPropagation(); onViewInExplorer(file.id); }}
                    className="hidden group-hover:block text-xs text-blue-500 hover:underline flex-shrink-0"
                  >
                    Explore
                  </button>
                )}
                {onViewSubgraph && (
                  <button
                    onClick={e => { e.stopPropagation(); onViewSubgraph(file.id); }}
                    className="hidden group-hover:block text-xs text-purple-500 hover:underline flex-shrink-0"
                  >
                    Subgraph
                  </button>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function countFiles(node: TreeNode): number {
  let count = node.files.length;
  for (const child of node.children.values()) {
    count += countFiles(child);
  }
  return count;
}

export function FileTreeView({ data, selectedFileId, onSelectFile, onViewInExplorer, onViewSubgraph, searchQuery = '' }: FileTreeViewProps) {
  const filteredNodes = useMemo(() => {
    if (!searchQuery.trim()) return data.graph.nodes;
    const q = searchQuery.toLowerCase();
    return data.graph.nodes.filter(n => n.name.toLowerCase().includes(q) || n.filePath.toLowerCase().includes(q));
  }, [data.graph.nodes, searchQuery]);

  const tree = useMemo(() => buildTree(filteredNodes), [filteredNodes]);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Legend — search is in page header (issue #566) */}
      <div className="px-4 py-1.5 border-b border-gray-100 dark:border-gray-800 flex items-center gap-3 text-xs text-gray-400">
        <span>↑ importers</span>
        <span>↓ imports</span>
        <span>⚠ circular</span>
        <span>B barrel</span>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-auto py-2">
        <TreeNodeRow
          node={tree}
          depth={0}
          selectedFileId={selectedFileId}
          onSelectFile={onSelectFile}
          onViewInExplorer={onViewInExplorer}
          onViewSubgraph={onViewSubgraph}
        />
        {filteredNodes.length === 0 && (
          <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
            No files match filter
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-400">
        {filteredNodes.length} files
      </div>
    </div>
  );
}
